import { useEffect, useRef, useState } from "react";
import Modal from "../../../../parts/modal-new";
import { STRIPE } from "../../../../constants";
import CheckboxGroup from "../../../../parts/inputs/checkbox-group";
import ModalFooter from "../../../../parts/modals/ModalFooter";
import Select from "../../../../parts/inputs/select";
import DropdownIconsCheck from "../../../../parts/inputs/dropdown-icons-check";
import DropdownCheckboxGroup from "../../../../parts/inputs/checkbox-group-dropdown";

const { SUPPORTED_CURRENCIES, ZERO_DECIMAL_CURRENCIES } = STRIPE;

export const CreatePlan = ({ plan = {}, type, user, taxRates }) => {
  const { id, nickname, metadata, unit_amount, recurring, currency } = plan;
  const { pressCustomBranding = {} } = user;
  const { gymLocations = [] } = pressCustomBranding;
  const isEdit = !!id;

  const [features, setFeatures] = useState(
    plan.features?.length ? plan.features : [""]
  );
  const [locations, setLocations] = useState(
    plan.facilikey?.length ? plan.facilikey : []
  );

  const [selectFormValues, setSelectFormValues] = useState({
    currency: currency || SUPPORTED_CURRENCIES[0],
    interval: recurring
      ? recurring.interval === "month" && recurring.interval_count === 3
        ? "quarter"
        : recurring.interval
      : "month",
    access: metadata?.access || "pull",
    text_rate: metadata?.tax_rate || "",
  });
  const handleSelectChange = ({ name, value }) => {
    setSelectFormValues({
      ...selectFormValues,
      [name]: value,
    });
  };
  const handleCheck = (e) => {
    const { checked, value } = e.target;
    setLocations((prevState) => {
      if (!checked) {
        return prevState.filter((location) => location !== value);
      }
      return [...prevState, value];
    });
  };

  const locationsWithKeys = gymLocations.filter((loc) => !!loc.facilikey);
  const checkboxes = locationsWithKeys?.length
    ? locationsWithKeys.map(({ facilikey, gymName, gymLocation }) => ({
        value: facilikey,
        title: `${gymName} (${gymLocation})`,
      }))
    : null;

  const inputRefs = useRef([]);
  useEffect(() => {
    // Focus the last input
    if (inputRefs.current.length > 0) {
      const lastInput = inputRefs.current[inputRefs.current.length - 1];
      if (lastInput && lastInput.value === "") lastInput.focus();
    }
  }, [features]);
  const handleRemoveFeature = (index) => {
    setFeatures((prevState) => {
      const newState = prevState.filter((_, idx) => idx !== index);
      inputRefs.current = inputRefs.current.filter((_, idx) => idx !== index);
      return newState;
    });
  };
  return (
    <Modal
      className="plan-modal"
      title={isEdit ? "Edit plan" : "Add new plan"}
      toClear={["plan", "type"]}
    >
      <form
        className="body"
        action="/actions/plan-manager/save"
        method="POST"
        data-noreset
        id="create-plan"
      >
        {isEdit && <input type="hidden" name="id" value={id} />}
        <input type="hidden" name="type" value={type} />
        <div className="row">
          <div className="group">
            <label htmlFor="nickname" className="label req">
              Name
            </label>
            <input
              type="text"
              name="nickname"
              placeholder="Enter plan name"
              required
              defaultValue={nickname || ""}
            />
          </div>
        </div>
        <div className="row">
          <div className="group">
            <label htmlFor="price" className="label req">
              Price
            </label>
            <input
              type="number"
              name="price"
              placeholder="Enter plan price"
              required
              step={0.01}
              min={0}
              disabled={isEdit}
              defaultValue={
                unit_amount
                  ? ZERO_DECIMAL_CURRENCIES.includes(currency.toUpperCase())
                    ? unit_amount
                    : unit_amount / 100
                  : ""
              }
            />
          </div>
          <div className="group">
            <label htmlFor="currency" className="label req">
              Currency
            </label>

            <Select
              name="currency"
              id="currency"
              disabled={isEdit}
              options={SUPPORTED_CURRENCIES.map((currency) => {
                return {
                  value: currency.toLowerCase(),
                  text: currency,
                };
              })}
              value={selectFormValues.currency}
              onChange={(newVal) =>
                handleSelectChange({ name: "currency", value: newVal })
              }
            />
          </div>
          <div className="group">
            <label htmlFor="interval" className="label req">
              Interval
            </label>
            <Select
              id="interval"
              name="interval"
              options={[
                { value: "month", text: "Month" },
                { value: "year", text: "Annual" },
                { value: "week", text: "weekly" },
                { value: "quarter", text: "Quarter" },
              ]}
              disabled={isEdit}
              onChange={(newVal) =>
                handleSelectChange({ name: "interval", value: newVal })
              }
              value={selectFormValues.interval}
            />
          </div>
        </div>
        {type === "press" && (
          <div className="row">
            <div className="group">
              <label htmlFor="access" className="label req">
                User Access
              </label>
              <Select
                name="access"
                id="access"
                disabled={isEdit}
                value={selectFormValues.access}
                onChange={(newVal) =>
                  handleSelectChange({ name: "access", value: newVal })
                }
                options={[
                  {
                    text: "Pull Access",
                    value: "pull",
                  },
                  {
                    text: "Push Access",
                    value: "push",
                  },
                ]}
              />
            </div>
            {taxRates?.length > 0 && (
              <div className="group">
                <label htmlFor="tax_rate" className="label req">
                  Tax Rate
                </label>
                <Select
                  name="tax_rate"
                  id="tax_rate"
                  value={selectFormValues["text_rate"]}
                  onChange={(newVal) =>
                    handleSelectChange({ name: "text_rate", value: newVal })
                  }
                  options={[
                    ...taxRates.map(({ id, display_name, percentage }) => {
                      return {
                        text: `${display_name} - ${percentage}%`,
                        value: id,
                      };
                    }),
                    { text: "None", value: "" },
                  ]}
                />
              </div>
            )}
            {checkboxes && (
              <div className="group">
                <DropdownCheckboxGroup
                  label="Gym location (Key access)"
                  name="facilikey"
                  onChange={(e) => handleCheck(e)}
                  checkboxes={checkboxes}
                  checkboxClass="gym-locations"
                  values={locations}
                />
              </div>
            )}
          </div>
        )}

        {type === "press" && (
          <div className="features-list">
            <div className="group">
              <label htmlFor="feature_1" className="label req">
                Included
              </label>
              {features.map((value, i) => {
                return (
                  <div className="input-row" key={`row${i}`}>
                    <input
                      type="text"
                      name={`feature_${i + 1}`}
                      placeholder="Enter plan features"
                      value={value}
                      required
                      ref={(el) => (inputRefs.current[i] = el)}
                      onChange={(e) => {
                        e.preventDefault();
                        const value = e.target.value;
                        setFeatures((prevState) => {
                          const newState = [...prevState];
                          newState[i] = value;
                          return newState;
                        });
                      }}
                      onKeyDown={(e) => {
                        const lastInput =
                          inputRefs.current[inputRefs.current.length - 1];
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                          if (lastInput?.value !== "")
                            setFeatures((prevState) => prevState.concat(""));
                        }
                      }}
                    />

                    {i !== 0 ? (
                      <button
                        data-button-type="text-white"
                        data-icon-only
                        onClick={(e) => {
                          e.preventDefault();
                          if (e.key !== "Enter") {
                            handleRemoveFeature(i);
                          }
                        }}
                      >
                        <img src="/img/new-icons/red-trash.svg" />
                      </button>
                    ) : (
                      <button disabled style={{ cursor: "default" }}></button>
                    )}
                  </div>
                );
              })}
            </div>
            <button
              data-button-type="outline"
              onClick={(e) => {
                e.preventDefault();
                setFeatures((prevState) => prevState.concat(""));
              }}
              disabled={
                features.length >= 10 ||
                !features[features.length - 1]?.length > 0
              }
            >
              <span>Add feature</span>
            </button>
          </div>
        )}
      </form>
      <ModalFooter
        isSubmitButton
        showAltButton={false}
        onClickButton={() => {}}
        classNameButton="button save"
        formId="create-plan"
      />
    </Modal>
  );
};
